import { useEffect, useRef } from "react";

/** Production widget "ecoeng.net enquiry" (managed). Sitekeys are public. */
export const TURNSTILE_SITE_KEY = "0x4AAAAAAFSusQyEldmEDrZ6";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  remove: (id: string) => void;
};

function loadScript(): Promise<TurnstileApi> {
  const w = window as Window & { turnstile?: TurnstileApi };
  if (w.turnstile) return Promise.resolve(w.turnstile);
  return new Promise((resolve, reject) => {
    let s = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }
    s.addEventListener("load", () => (w.turnstile ? resolve(w.turnstile) : reject(new Error("turnstile"))));
    s.addEventListener("error", () => reject(new Error("turnstile")));
  });
}

/**
 * Cloudflare Turnstile widget (managed). Reports the token through onToken.
 * Tokens are single-use: remount it (change its key) to get a fresh one after a failed send.
 */
export function TurnstileWidget({ onToken }: { onToken: (token: string | null) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let widgetId: string | null = null;
    let api: TurnstileApi | null = null;
    let cancelled = false;
    loadScript()
      .then((t) => {
        if (cancelled || !ref.current) return;
        api = t;
        widgetId = t.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action: "enquiry",
          callback: (token: string) => onToken(token),
          "expired-callback": () => onToken(null),
          "error-callback": () => onToken(null),
        });
      })
      .catch(() => onToken(null));
    return () => {
      cancelled = true;
      if (api && widgetId) api.remove(widgetId);
    };
  }, [onToken]);
  return <div ref={ref} className="cf-turnstile" style={{ margin: "0.75rem 0" }} />;
}
